import React, { useEffect, useRef, useState } from "react";
import "../../../Assets/styles/LandingStyles/CopyrightFooter.css";
import { useLanguage } from "../../../Core/Translations/LanguageContext";
import PoweredByDefault from '../../../Assets/images/SALogo.png';
import PoweredByBlackText from '../../../Assets/images/SALogoBlackText.png';

export const CopyrightFooter: React.FC = () => {
    const { t } = useLanguage();
    const sealContainerRef = useRef<HTMLDivElement>(null);
    const [sealStatus, setSealStatus] = useState<"loading" | "loaded" | "error">("loading");
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    // The logic to detect small screen sizes remains the same (dynamic)
    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 970px)');
        const handleMediaChange = (e: MediaQueryListEvent) => {
            setIsSmallScreen(e.matches);
        };

        setIsSmallScreen(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleMediaChange);

        return () => mediaQuery.removeEventListener('change', handleMediaChange);
    }, []);

    // THE SEAL LOADING LOGIC REMAINS STATIC AND WORKING
    useEffect(() => {
        const loadSeal = () => {
            if (!sealContainerRef.current) return;

            const sealElement = document.createElement("span");
            sealElement.id = "siteseal";

            const sealContentDiv = sealContainerRef.current.querySelector(".seal-content");
            if (sealContentDiv) {
                sealContentDiv.appendChild(sealElement);
            }

            const script = document.createElement("script");
            script.src = "https://seal.godaddy.com/getSeal?sealID=vTe5c1dfcXAPJXEQdibfwglLlZpR7wJV6Uz5NUWXbrxx6s0ojR87ggwBZScv";
            script.async = true;
            script.defer = true;
            script.type = "text/javascript";

            const handleLoad = () => {
                setTimeout(() => {
                    if (sealElement.children.length > 0 || sealElement.innerHTML.trim() !== "") {
                        setSealStatus("loaded");
                    } else {
                        setSealStatus("error");
                    }
                }, 1000);
            };

            const handleError = () => setSealStatus("error");
            script.addEventListener("load", handleLoad);
            script.addEventListener("error", handleError);
            document.body.appendChild(script);

            return () => {
                script.removeEventListener("load", handleLoad);
                script.removeEventListener("error", handleError);
                if (document.body.contains(script)) {
                    document.body.removeChild(script);
                }
                if (sealElement.parentNode) {
                    sealElement.parentNode.removeChild(sealElement);
                }
            };
        };

        const timer = setTimeout(loadSeal, 100);
        return () => clearTimeout(timer);
    }, []);

    // Modal closing logic remains the same
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && showPrivacyModal) {
                setShowPrivacyModal(false);
            }
        };

        if (showPrivacyModal) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [showPrivacyModal]);

    return (
        <>
            <footer className="footer-copyright" role="contentinfo">
                <div className="footer-content">
                    {/* The entire seal-container section is what you want static/reliable */}
                    <div
                        ref={sealContainerRef}
                        className="seal-container"
                        aria-label={sealStatus === "loaded" ? "Security verification seal" : "Security status"}
                        role={sealStatus === "loaded" ? "img" : "status"}
                    >
                        <div className={`loading-indicator ${sealStatus !== "loading" ? "hidden" : ""}`}>
                            <div className="pulsing-dot" />
                            <span className="loading-text">Verifying security...</span>
                        </div>

                        {sealStatus === "error" && (
                            <span className="seal-error" role="alert">Security seal unavailable</span>
                        )}

                        <div className={`seal-content ${sealStatus === "loaded" ? "seal-visible" : "seal-hidden"}`} />

                        <span id="gd-seal-description" hidden>
                            GoDaddy website security verification seal
                        </span>
                    </div>

                    {/* The rest of the footer uses translations */}
                    <div className="copyright-section">
                        <div className="copyright-text">
                            {t.copywright.text}
                        </div>
                        <button 
                            className="privacy-policy-link"
                            onClick={() => setShowPrivacyModal(true)}
                            // This aria-label is static, but the button text is dynamic:
                            aria-label="View Privacy Policy" 
                        >
                            {/* Uses the dynamic translation for the button text */}
                            {t.privacyPolicy.modalTitle} 
                        </button>
                    </div>

                    <a
                        className="powered-by-link"
                        href="https://www.softwarearchitectsjm.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img
                            src={isSmallScreen ? PoweredByBlackText : PoweredByDefault}
                            alt="Powered by Software Architects Limited"
                            className="powered-by-logo"
                        />
                    </a>
                </div>
            </footer>

            {showPrivacyModal && (
                <div 
                    className="privacy-modal-overlay"
                    onClick={() => setShowPrivacyModal(false)}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="privacy-modal-title"
                >
                    <div 
                        className="privacy-modal-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="privacy-modal-header">
                            {/* Uses dynamic translation for the modal title */}
                            <h2 id="privacy-modal-title">{t.privacyPolicy.modalTitle}</h2> 
                            <button 
                                className="privacy-modal-close"
                                onClick={() => setShowPrivacyModal(false)}
                                aria-label="Close Privacy Policy"
                            >
                                ×
                            </button>
                        </div>
                        <div className="privacy-modal-body">
                            {/* ALL MODAL CONTENT USES DYNAMIC TRANSLATIONS (t) */}
                            <section className="privacy-section">
                                <h3>{t.privacyPolicy.sections.introduction.title}</h3>
                                <p>{t.privacyPolicy.sections.introduction.p1}</p>
                                <p>{t.privacyPolicy.sections.introduction.p2}</p>
                            </section>

                            <section className="privacy-section">
                                <h3>{t.privacyPolicy.sections.informationWeCollect.title}</h3>
                                <p>{t.privacyPolicy.sections.informationWeCollect.p1}</p>
                                <ul>
                                    {/* These list items are intentionally formatted to use the t object as keys */}
                                    <li><strong>{t.privacyPolicy.sections.informationWeCollect.list.usageData}</strong></li>
                                    <li><strong>{t.privacyPolicy.sections.informationWeCollect.list.deviceInfo}</strong></li>
                                    <li><strong>{t.privacyPolicy.sections.informationWeCollect.list.cookies}</strong></li>
                                </ul>
                            </section>

                            <section className="privacy-section">
                                <h3>{t.privacyPolicy.sections.howWeUseInfo.title}</h3>
                                <p>{t.privacyPolicy.sections.howWeUseInfo.p1}</p>
                                <ul>
                                    <li><strong>{t.privacyPolicy.sections.howWeUseInfo.list.gameImprovement}</strong></li>
                                    <li><strong>{t.privacyPolicy.sections.howWeUseInfo.list.performanceAnalytics}</strong></li>
                                    <li><strong>{t.privacyPolicy.sections.howWeUseInfo.list.personalization}</strong></li>
                                    <li><strong>{t.privacyPolicy.sections.howWeUseInfo.list.technicalSupport}</strong></li>
                                </ul>
                            </section>

                            <section className="privacy-section">
                                <h3>{t.privacyPolicy.sections.cookies.title}</h3>
                                <p>{t.privacyPolicy.sections.cookies.p1}</p>
                                
                                <p><strong>{t.privacyPolicy.sections.cookies.performanceCookies.title}</strong></p>
                                <p>{t.privacyPolicy.sections.cookies.performanceCookies.description}</p>
                                
                                <p><strong>{t.privacyPolicy.sections.cookies.advertisingCookies.title}</strong></p>
                                <ul>
                                    {/* This is the map that needs a valid array in the t object */}
                                    {t.privacyPolicy.sections.cookies.advertisingCookies.list.map((item, index) => (
                                        <li key={index}>{item}</li>
                                    ))}
                                </ul>
                                
                                <p>{t.privacyPolicy.sections.cookies.p2}</p>
                            </section>

                            <section className="privacy-section">
                                <h3>{t.privacyPolicy.sections.thirdParty.title}</h3>
                                <p>{t.privacyPolicy.sections.thirdParty.p1}</p>
                                
                                <p><strong>{t.privacyPolicy.sections.thirdParty.gemini.title}</strong></p>
                                <p>{t.privacyPolicy.sections.thirdParty.gemini.description}</p>
                                
                                <p><strong>{t.privacyPolicy.sections.thirdParty.youtube.title}</strong></p>
                                <p>{t.privacyPolicy.sections.thirdParty.youtube.description}</p>
                                
                                <p><strong>{t.privacyPolicy.sections.thirdParty.advertising.title}</strong></p>
                                <p>{t.privacyPolicy.sections.thirdParty.advertising.description}</p>
                            </section>

                            <section className="privacy-section">
                                <h3>{t.privacyPolicy.sections.dataSecurity.title}</h3>
                                <p>{t.privacyPolicy.sections.dataSecurity.p1}</p>
                                <p>{t.privacyPolicy.sections.dataSecurity.p2}</p>
                            </section>

                            <section className="privacy-section">
                                <h3>{t.privacyPolicy.sections.privacyRights.title}</h3>
                                <p>{t.privacyPolicy.sections.privacyRights.p1}</p>
                                <ul>
                                    <li><strong>{t.privacyPolicy.sections.privacyRights.list.access}</strong></li>
                                    <li><strong>{t.privacyPolicy.sections.privacyRights.list.correction}</strong></li>
                                    <li><strong>{t.privacyPolicy.sections.privacyRights.list.deletion}</strong></li>
                                    <li><strong>{t.privacyPolicy.sections.privacyRights.list.optOut}</strong></li>
                                    <li><strong>{t.privacyPolicy.sections.privacyRights.list.portability}</strong></li>
                                </ul>
                            </section>

                            <section className="privacy-section">
                                <h3>{t.privacyPolicy.sections.childrenPrivacy.title}</h3>
                                <p>{t.privacyPolicy.sections.childrenPrivacy.p1}</p>
                            </section>

                            <section className="privacy-section">
                                <h3>{t.privacyPolicy.sections.changes.title}</h3>
                                <p>{t.privacyPolicy.sections.changes.p1}</p>
                                <p>{t.privacyPolicy.sections.changes.p2}</p>
                            </section>

                            <section className="privacy-section">
                                <h3>{t.privacyPolicy.sections.contact.title}</h3>
                                <p>{t.privacyPolicy.sections.contact.p1}</p>
                                <p><strong>{t.privacyPolicy.sections.contact.email}</strong></p>
                                <p><strong>{t.privacyPolicy.sections.contact.website}</strong></p>
                                <p>{t.privacyPolicy.sections.contact.p2}</p>
                            </section>

                            <div className="privacy-footer">
                                <p><em>{t.privacyPolicy.footer.lastUpdated}</em></p>
                                <p><em>{t.privacyPolicy.footer.effectiveDate}</em></p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CopyrightFooter;